import React, { useState, useEffect } from 'react';
import Header from '../components/Header.jsx';
import { eventsAPI } from '../services/api';
import Footer from '../components/Footer.jsx';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await eventsAPI.getAll();
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const openEventModal = (event) => {
    setSelectedEvent(event);
    setCurrentImageIndex(0);
    setShowModal(true);
  };

  const closeEventModal = () => {
    setSelectedEvent(null);
    setShowModal(false);
  };

  const nextImage = () => {
    if (selectedEvent && selectedEvent.imageUrls) {
      setCurrentImageIndex((prev) => (prev + 1) % selectedEvent.imageUrls.length);
    }
  };

  const prevImage = () => {
    if (selectedEvent && selectedEvent.imageUrls) {
      setCurrentImageIndex((prev) => (prev - 1 + selectedEvent.imageUrls.length) % selectedEvent.imageUrls.length);
    }
  };

  const inquireViaWhatsApp = (event) => {
    const message = encodeURIComponent(
      `Hi! I would like to know more about:\n\nEvent: ${event.title}\nDate: ${formatDate(event.event_date)}\n\nPlease provide more details.`
    );
    window.open(`https://wa.me/918879424647?text=${message}`, '_blank');
  };

  return (
    <div>
      <Header />
      <main>
        <section className="section">
          <h1 style={{ textAlign: 'center', color: 'var(--maroon)', fontFamily: "'Playfair Display',serif", fontSize: '36px', marginBottom: '10px' }}>
            Upcoming Events
          </h1>
          <p style={{ textAlign: 'center', color: 'var(--muted)', marginBottom: '30px' }}>
            Join us for spiritual gatherings, workshops, and special ceremonies and Events
          </p>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px', color: 'var(--muted)' }}>
              Loading events...
            </div>
          ) : events.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '50px' }}>
              <h3 style={{ color: 'var(--muted)' }}>No upcoming events</h3>
              <p style={{ color: 'var(--muted)' }}>Stay tuned for future events and workshops!</p>
            </div>
          ) : (
            <div className="grid-3">
              {events.map((event) => {
                // Handle both old format (images string) and new format (imageUrls array)
                let imageArray = [];
                if (event.imageUrls && Array.isArray(event.imageUrls)) {
                  imageArray = event.imageUrls;
                } else if (event.images && typeof event.images === 'string') {
                  imageArray = event.images.split(',').map(img => img.trim());
                }
                
                // Filter for valid images - Base64 must be longer than just the prefix
                const validImages = imageArray.filter(img => 
                  img && (
                    img.startsWith('http://') || 
                    img.startsWith('https://') || 
                    (img.startsWith('data:image') && img.length > 50)
                  )
                );
                
                const primaryImage = validImages[0] || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="250"%3E%3Crect width="400" height="250" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="18" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';

                return (
                  <div 
                    key={event.event_id} 
                    className="card" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => openEventModal(event)}
                  >
                    <div style={{
                      width: '100%',
                      height: '180px',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      marginBottom: '12px'
                    }}>
                      <img
                        src={primaryImage}
                        alt={event.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { 
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="250"%3E%3Crect width="400" height="250" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="18" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                    <h3 style={{ margin: '0 0 12px', color: 'var(--maroon)' }}>{event.title}</h3>

                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{ fontSize: '18px' }}>📅</span>
                        <span style={{ color: 'var(--muted)', fontSize: '14px' }}>
                          {formatDate(event.event_date)}
                        </span>
                      </div>
                      {event.event_time && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <span style={{ fontSize: '18px' }}>🕐</span>
                          <span style={{ color: 'var(--muted)', fontSize: '14px' }}>
                            {formatTime(event.event_time)}
                          </span>
                        </div>
                      )}
                      {event.location && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '18px' }}>📍</span>
                          <span style={{ color: 'var(--muted)', fontSize: '14px' }}>
                            {event.location}
                          </span>
                        </div>
                      )}
                    </div>

                    <p style={{ color: 'var(--muted)', fontSize: '14px', margin: '12px 0' }}>
                      {event.event_description}
                    </p>

                    <button
                      className="book"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent modal from opening
                        inquireViaWhatsApp(event);
                      }}
                      style={{ width: '100%', marginTop: '12px' }}
                    >
                      Inquire via WhatsApp
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Event Detail Modal */}
        {showModal && selectedEvent && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }}
            onClick={closeEventModal}
          >
            <div 
              style={{
                background: '#fff',
                borderRadius: '16px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative',
                boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeEventModal}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  background: 'var(--maroon)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: '35px',
                  height: '35px',
                  fontSize: '18px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1
                }}
              >
                ×
              </button>

              <div style={{ position: 'relative' }}>
                {(() => {
                  let imageArray = [];
                  if (selectedEvent.imageUrls && Array.isArray(selectedEvent.imageUrls)) {
                    imageArray = selectedEvent.imageUrls;
                  } else if (selectedEvent.images && typeof selectedEvent.images === 'string') {
                    imageArray = selectedEvent.images.split(',').map(img => img.trim());
                  }
                  
                  const validImages = imageArray.filter(img => 
                    img && (
                      img.startsWith('http://') || 
                      img.startsWith('https://') || 
                      (img.startsWith('data:image') && img.length > 50)
                    )
                  );
                  
                  const currentImage = validImages[currentImageIndex] || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="600" height="250"%3E%3Crect width="600" height="250" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="24" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';

                  return (
                    <>
                      <img
                        src={currentImage}
                        alt={selectedEvent.title}
                        style={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: '400px',
                          objectFit: 'contain',
                          borderRadius: '16px 16px 0 0'
                        }}
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="600" height="250"%3E%3Crect width="600" height="250" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="24" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      {validImages.length > 1 && (
                        <>
                          <button
                            onClick={prevImage}
                            style={{
                              position: 'absolute',
                              left: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'rgba(0,0,0,0.5)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '50%',
                              width: '40px',
                              height: '40px',
                              fontSize: '20px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ‹
                          </button>
                          <button
                            onClick={nextImage}
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'rgba(0,0,0,0.5)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '50%',
                              width: '40px',
                              height: '40px',
                              fontSize: '20px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ›
                          </button>
                          <div style={{
                            position: 'absolute',
                            bottom: '10px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(0,0,0,0.5)',
                            color: '#fff',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            {currentImageIndex + 1} / {validImages.length}
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>

              <div style={{ padding: '30px' }}>
                <h2 style={{
                  margin: '0 0 20px',
                  color: 'var(--maroon)',
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '28px'
                }}>
                  {selectedEvent.title}
                </h2>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '12px',
                    color: 'var(--muted)',
                    fontSize: '16px'
                  }}>
                    <span>📅</span>
                    <strong>Date:</strong>
                    <span>{formatDate(selectedEvent.event_date)}</span>
                  </div>

                  {selectedEvent.event_time && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '12px',
                      color: 'var(--muted)',
                      fontSize: '16px'
                    }}>
                      <span>🕐</span>
                      <strong>Time:</strong>
                      <span>{formatTime(selectedEvent.event_time)}</span>
                    </div>
                  )}

                  {selectedEvent.location && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      color: 'var(--muted)',
                      fontSize: '16px'
                    }}>
                      <span>📍</span>
                      <strong>Location:</strong>
                      <span>{selectedEvent.location}</span>
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{
                    margin: '0 0 15px',
                    color: 'var(--maroon)',
                    fontSize: '20px'
                  }}>
                    Description
                  </h3>
                  <p style={{
                    color: 'var(--muted)',
                    lineHeight: '1.6',
                    fontSize: '16px',
                    margin: 0
                  }}>
                    {selectedEvent.description || 'No description available for this event.'}
                  </p>
                </div>

                <button
                  className="book"
                  onClick={() => inquireViaWhatsApp(selectedEvent)}
                  style={{ width: '100%' }}
                >
                  Inquire via WhatsApp
                </button>
              </div>
            </div>
          </div>
        )}
{/* If she ever invites me to an event, I wont say no tho. */}
        {/* <footer style={{ marginTop: '50px' }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center', color: 'var(--muted)' }}>
            © {new Date().getFullYear()} Shree Rashi Jyotish.
          </div>
        </footer> */}

        <Footer />
      </main>
    </div>
  );
};

export default Events;